import { MetaMaskSDK, SDKProvider } from "@metamask/sdk";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { Event } from "../../shared/event-system/Event";
import { ChainUtils } from "../utils/ChainUtils";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { utils } from "pixi.js";
import { TickerDelay } from "../../tools/TickerDelay";
import { Web3Config } from "../config/Web3Config";
import { GameInfo } from "../../analytics/GameInfo";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { VisibilityTimeoutObserver } from "../../tools/VisibilityTimeoutObserver";
import { EthereumProvider } from "./rpc/EthereumProvider";
import { RPCError } from "./rpc/RPCError";
import { RPCErrorCode } from "./rpc/RPCErrorCode";
import { RPCProvider } from "./rpc/RPCProvider";
import { ExternalWalletType } from "./ExternalWalletType";
import { IExternalWallet } from "./IExternalWallet";

export class MetaMask implements IExternalWallet {
    private static readonly LAST_CONNECTED_STATE_KEY = "mm_was_connected";
    private static readonly UPDATE_ADDRESS_TIMEOUT = 3000;

    public readonly onDataChanged: Event<void>;

    private readonly variableSaver: VariableSaver;
    private readonly web3Config: Web3Config;
    private readonly logger: ILogger;
    private readonly metaMaskSDK: MetaMaskSDK;
    private readonly timeoutObserver: VisibilityTimeoutObserver;

    private sdkProvider: SDKProvider;
    private rpcProvider: RPCProvider;

    private initializePCS: PromiseCompletionSource<void>;
    private connectPCS: PromiseCompletionSource<Array<string>>;
    private addChainPCS: PromiseCompletionSource<void>;
    private switchNetworkPCS: PromiseCompletionSource<void>;
    private signMessagePCS: PromiseCompletionSource<string>;
    private burnNFTPCS: PromiseCompletionSource<string>;

    private addressesValue: Array<string>;
    private chainIdValue: string;

    public get type(): ExternalWalletType {
        return ExternalWalletType.METAMASK;
    }

    public get connected(): boolean {
        return this.sdkProvider != null && this.addresses?.length > 0;
    }

    public get addresses(): Array<string> {
        const selectedAddressIndex = this.addressesValue?.indexOf(this.sdkProvider.getSelectedAddress()) ?? -1;
        if (selectedAddressIndex <= 0) {
            return this.addressesValue;
        }
        this.addressesValue.splice(selectedAddressIndex, 1);
        this.addressesValue.splice(0, 0, this.sdkProvider.getSelectedAddress());
    }

    public get chainId(): string {
        return this.chainIdValue;
    }

    public get supportedChainSelected(): boolean {
        return this.chainIdValue === ChainUtils.idToHex(this.web3Config.targetChain.id);
    }

    public get provider(): EthereumProvider {
        return this.sdkProvider;
    }

    public constructor(web3Config: Web3Config, variableSaver: VariableSaver, gameInfo: GameInfo, logger: ILogger, timeoutObserver: VisibilityTimeoutObserver) {
        this.web3Config = web3Config;
        this.variableSaver = variableSaver;
        this.logger = logger;
        this.timeoutObserver = timeoutObserver;
        this.onDataChanged = new Event();
        this.metaMaskSDK = new MetaMaskSDK({
            dappMetadata: {
                name: this.web3Config.appName,
                url: location.origin
            },
            infuraAPIKey: this.web3Config.infuraApiKey,
            extensionOnly: !utils.isMobile.any,
            logging: {
                developerMode: gameInfo.debug,
                eciesLayer: gameInfo.debug,
                keyExchangeLayer: gameInfo.debug,
                plaintext: gameInfo.debug,
                remoteLayer: gameInfo.debug,
                sdk: gameInfo.debug,
                serviceLayer: gameInfo.debug
            }
        });

        this.onAccountChanged = this.onAccountChanged.bind(this);
        this.onChainChanged = this.onChainChanged.bind(this);
        this.onConnect = this.onConnect.bind(this);
        this.onDisconnect = this.onDisconnect.bind(this);
        this.initialize();
    }

    public async autoConnect(): Promise<boolean> {
        this.logger.debug("MetaMask auto connect");
        try {
            if (!this.variableSaver.exist(MetaMask.LAST_CONNECTED_STATE_KEY) || !this.variableSaver.getBoolean(MetaMask.LAST_CONNECTED_STATE_KEY)) {
                this.logger.debug("Auto connect cancelled, wasnt connected in previous session");
                return false;
            }
            this.logger.debug("Attemp of auto connect metamask");
            await this.waitForInitialize();
            await this.updateAddress();
            if (this.addressesValue == null) {
                return false;
            }
            await this.updateChainId();
            return true;
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on auto connect metamask, ${e}`);
            return false;
        }
    }

    public async connect(): Promise<boolean> {
        this.logger.debug("MetaMask connect");
        if (this.connected) {
            this.logger.debug("MetaMask already connected");
            return true;
        }
        try {
            await this.waitForInitialize();
            this.connectPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.connectPCS, () => !this.connected);
            const accounts = await this.requestWithPCS(this.rpcProvider.requestAccounts(), () => this.connectPCS);
            this.timeoutObserver.removeOperation(this.connectPCS);
            this.connectPCS = null;
            this.addressesValue = accounts;
            if (!this.connected) {
                return false;
            }
            if (this.chainIdValue == null) {
                await this.updateChainId();
            }
            this.variableSaver.saveBoolean(MetaMask.LAST_CONNECTED_STATE_KEY, this.connected);
            return this.connected;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.connectPCS);
            this.connectPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug(`User reject connection`);
                return false;
            }
            this.logger.error(`Exception on connect MetaMask: `, error);
            await this.disconnect();
            return false;
        }
    }

    public async switchNetwork(): Promise<boolean> {
        this.logger.debug("MetaMask switch network");
        if (!this.connected) {
            this.logger.debug(`Metamask Unable to switch network, not connected`);
            return false;
        }
        try {
            await this.waitForInitialize();
            await this.updateChainId();
            if (this.supportedChainSelected) {
                this.logger.debug(`Metamask Supported already chain selected`);
                return true;
            }
            this.switchNetworkPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.switchNetworkPCS, () => !this.supportedChainSelected);
            await this.requestWithPCS(this.rpcProvider.switchChain(this.web3Config.targetChain.id), () => this.switchNetworkPCS);
            this.timeoutObserver.removeOperation(this.switchNetworkPCS);
            this.switchNetworkPCS = null;
            return this.supportedChainSelected;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.switchNetworkPCS);
            this.switchNetworkPCS = null;
            if (error instanceof RPCError) {
                switch (error.code) {
                    case RPCErrorCode.UNRECOGNIZED_CHAIN:
                        this.logger.debug(`MetaMask UNRECOGNIZED_CHAIN on switchNetwork`);
                        if (!await this.addChain()) {
                            this.logger.debug(`MetaMask Add chain failed`);
                            return this.supportedChainSelected;
                        }
                        if (this.supportedChainSelected) {
                            this.logger.debug(`MetaMask Supported already chain selected`);
                            return true;
                        }
                        return await this.switchNetwork();
                    case RPCErrorCode.REJECTED:
                        this.logger.debug(`User reject switch chain`);
                        return false;
                }
            }

            this.logger.error(`Exception on switch network: `, error);
            return false;
        }
    }

    public async signMessage(address: string, nonce: string): Promise<string> {
        this.logger.debug(`MetaMask sign message, nonce: ${nonce}`);
        if (!this.connected) {
            this.logger.debug(`MetaMask unable to sign message, not connected`);
            return null;
        }
        const addressIndex = this.addresses.findIndex((connectedAddress) => connectedAddress.toLowerCase() == address.toLowerCase())
        if (addressIndex === -1) {
            this.logger.error(`Attemp of sign message using not compatible address (${address}), list of connected addresses: ${this.addresses}`);
            return null;
        }
        address = this.addresses[addressIndex];
        try {
            await this.waitForInitialize();
            this.signMessagePCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.signMessagePCS);
            const signature = await this.requestWithPCS(this.rpcProvider.signMessage(nonce, address), () => this.signMessagePCS);
            this.timeoutObserver.removeOperation(this.signMessagePCS);
            this.signMessagePCS = null;
            return signature;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.signMessagePCS);
            this.signMessagePCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug(`User reject sign message`);
                return null;
            }
            this.logger.error(`Exception on sign message: `, error);
            return null;
        }
    }

    public async burnNFT(address: string, burnData: string): Promise<string> {
        this.logger.debug(`MetaMask burn nft`);
        if (!this.connected) {
            this.logger.debug(`Attemp of burn nft from not connected wallet`);
            return null;
        }
        if (!this.addresses.includes(address)) {
            this.logger.error(`Attemp of burn nft using not compatible address (${address}), list of connected addresses: ${this.addresses}`);
            return null;
        }
        try {
            await this.waitForInitialize();
            this.burnNFTPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.burnNFTPCS);
            const result = await this.requestWithPCS(
                this.rpcProvider.sendTransaction(
                    this.web3Config.packagesCollectionAddress,
                    address,
                    burnData),
                () => this.burnNFTPCS
            );
            this.timeoutObserver.removeOperation(this.burnNFTPCS);
            this.burnNFTPCS = null;
            return result;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.burnNFTPCS);
            this.burnNFTPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug("User reject burn");
                return null;
            }
            this.logger.error("Exception on burn nft: ", error);
            return null;
        }
    }

    public async disconnect(): Promise<void> {
        this.logger.debug(`MetaMask disconnect`);
        try {
            if (!utils.isMobile.any && this.provider != null) {
                await this.rpcProvider.revokePermissions();
            }
            this.addressesValue = null;
            this.chainIdValue = null;
            this.metaMaskSDK.terminate();
        }
        catch (error: unknown) {
            this.logger.error(`Error on disconnect: `, error);
        }
        finally {
            this.variableSaver.saveBoolean(MetaMask.LAST_CONNECTED_STATE_KEY, false);
            this.logger.debug(`MetaMask clear provider`);
            this.initializePCS?.error(`User disconnected`);
            this.connectPCS?.error(`User disconnected`);
            this.switchNetworkPCS?.error(`User disconnected`);
            this.signMessagePCS?.error(`User disconnected`);
            this.addChainPCS?.error(`User disconnected`);
            this.sdkProvider?.removeAllListeners();
            this.sdkProvider = null;
            this.rpcProvider = null;
            this.onDataChanged.emit();
        }
    }


    private async initialize(): Promise<void> {
        this.initializePCS = new PromiseCompletionSource();
        this.logger.debug(`Metamask initialization`);
        try {
            await this.metaMaskSDK.init();
            await this.initializeProvider();
        }
        catch (e: unknown) {
            this.logger.error(`Metamsk init error`, e);
            await this.disconnect();
        }
        finally {
            this.initializePCS = null;
        }
    }

    private async addChain(): Promise<boolean> {
        this.logger.debug("MetaMask add chain");
        if (!this.connected) {
            this.logger.debug(`Unable to add chain, not connected`);
            return false;
        }
        try {
            await this.waitForInitialize();
            this.addChainPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.addChainPCS);
            await this.requestWithPCS(this.rpcProvider.addChain(this.web3Config.targetChain), () => this.addChainPCS);
            this.timeoutObserver.removeOperation(this.addChainPCS);
            this.addChainPCS = null;
            return true;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.addChainPCS);
            this.addChainPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug(`User rejected add chain`);
                return false;
            }
            this.logger.error(`Exception on add chain`, error);
            return false;
        }
    }

    private async initializeProvider(): Promise<void> {
        try {
            this.logger.debug(`MetaMask initialize provider`);
            this.sdkProvider = this.metaMaskSDK.getProvider();
            if (this.sdkProvider == null) {
                this.logger.debug(`Provider initialization failed, provider is null`);
                return;
            }
            this.sdkProvider.addListener('accountsChanged', this.onAccountChanged);
            this.sdkProvider.addListener('chainChanged', this.onChainChanged);
            this.sdkProvider.addListener('connect', this.onConnect);
            this.sdkProvider.addListener('disconnect', this.onDisconnect);
            this.logger.debug(`MetaMask provider initialized, provider data: connected: ${this.sdkProvider.isConnected()}, address: ${this.sdkProvider.getSelectedAddress()}, chainId: ${this.sdkProvider.getChainId()}`);
            this.rpcProvider = new RPCProvider(this.sdkProvider);
            this.onDataChanged.emit();
            this.initializePCS?.complete();
        }
        catch (e: unknown) {
            this.logger.error(`Error on initialize provider, disconnect: `, e);
            await this.disconnect();
        }
    }

    private async updateChainId(): Promise<void> {
        this.logger.debug(`MetaMask update chain id`);
        try {
            await this.waitForInitialize();
            this.chainIdValue = await this.rpcProvider.getChainId() ?? this.chainIdValue;
            this.logger.debug(`Update chain id result: ${this.chainIdValue}`);
            if (this.supportedChainSelected) {
                this.switchNetworkPCS?.complete();
                this.addChainPCS?.complete();
            }
            this.onDataChanged.emit();
        }
        catch (e: unknown) {
            this.logger.error(`Exception on updating address: `, e);
        }
    }

    private async updateAddress(): Promise<void> {
        this.logger.debug(`MetaMask Updating address`);
        try {
            await this.waitForInitialize();
            let updateAddressPCS = new PromiseCompletionSource<Array<string>>();

            TickerDelay.delayCallback(async () => {
                if (this.addresses.length > 0) {
                    updateAddressPCS?.complete(this.addresses);
                    return;
                }
                this.logger.debug(`Update address timeout, disconnect`);
                await this.disconnect();
                updateAddressPCS?.error("Update address timeout");
            }, MetaMask.UPDATE_ADDRESS_TIMEOUT);

            this.logger.debug("MetaMask update address");
            const accounts = await this.requestWithPCS(this.rpcProvider.getAccounts(), () => updateAddressPCS);
            updateAddressPCS = null;
            this.addressesValue = accounts;
            if (this.connected) {
                this.connectPCS?.complete(accounts);
            }
            else {
                this.connectPCS?.error(new Error("eth_accounts, account is empty"));
            }
            this.onDataChanged.emit();
        }
        catch (e: unknown) {
            this.logger.error(`Exception on updating address: `, e);
        }
    }

    private async onAccountChanged(accounts: Array<string>): Promise<void> {
        this.logger.debug(`MetaMask account changed: ${accounts}`);
        this.addressesValue = accounts;
        if (this.connected && (this.addressesValue == null || this.addressesValue.length === 0)) {
            this.logger.debug(`Account changed, no address, disconnect`);
            await this.disconnect();
        }
        else {
            this.connectPCS?.complete(accounts);
        }
    }

    private async onChainChanged(chainId: string): Promise<void> {
        this.logger.debug(`MataMask chain changed: ${chainId}`);
        this.chainIdValue = chainId;
        if (this.supportedChainSelected) {
            this.addChainPCS?.complete();
            this.switchNetworkPCS?.complete();
        }
        this.onDataChanged.emit();
    }

    private async onConnect(connectInfo: { chainId: string }): Promise<void> {
        this.logger.debug(`MetaMask connected, connectInfo: ${JSON.stringify(connectInfo)}`);
        this.chainIdValue = this.chainIdValue;
        const selectedAddress = this.sdkProvider?.getSelectedAddress();
        this.addressesValue = this.addressesValue?.length > 0 ? this.addressesValue : selectedAddress == null ? null : selectedAddress.length == 0 ? null : [selectedAddress];
        if (this.addressesValue != null) {
            this.connectPCS?.complete(this.addressesValue);
        }
        if (this.supportedChainSelected) {
            this.addChainPCS?.complete();
            this.switchNetworkPCS?.complete();
        }
        this.onDataChanged.emit();
    }

    private async onDisconnect(error: { message: string, code: number, data: unknown }): Promise<void> {
        this.logger.debug(`MetaMask disconnect, reason: ${error}`);
        await this.disconnect();
    }

    private async waitForInitialize(): Promise<void> {
        if (this.initializePCS != null) {
            this.logger.debug(`Await initialize metamask`);
            return await this.initializePCS.result;
        }
        else if (this.provider == null) {
            this.initializePCS = new PromiseCompletionSource();
            await this.initializeProvider();
            this.initializePCS = null;
            return;
        }
        return Promise.resolve();
    }

    private async requestWithPCS<T>(operation: Promise<T>, pcsGetter: () => PromiseCompletionSource<T>): Promise<T> {
        operation.then((result: T) => {
            this.logger.debug(`Operation completed with result: ${result}`);
            const pcs = pcsGetter();
            pcs?.complete(result);
        }, (error: unknown) => {
            this.logger.debug(`Operation failed reason: ${JSON.stringify(error)}`);
            const pcs = pcsGetter();
            pcs?.error(error);
        });
        return await pcsGetter()?.result;
    }
}